import { Platform } from 'react-native';

const CACHE_KEY = 'USER_LANGUAGE_PREFERENCE_CACHE';

interface CachedUserLanguage {
  smartuserId: string;
  language: string;
  cachedAt: number;
}

const getStorageItem = async (key: string): Promise<string | null> => {
  if (Platform.OS === 'web') {
    return localStorage.getItem(key);
  } else {
    const { getItemAsync } = await import('expo-secure-store');
    return await getItemAsync(key);
  }
};

const setStorageItem = async (key: string, value: string): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.setItem(key, value);
  } else {
    const { setItemAsync } = await import('expo-secure-store');
    await setItemAsync(key, value);
  }
};

const deleteStorageItem = async (key: string): Promise<void> => {
  if (Platform.OS === 'web') {
    localStorage.removeItem(key);
  } else {
    const { deleteItemAsync } = await import('expo-secure-store');
    await deleteItemAsync(key);
  }
};

class UserLanguagePreferenceCache {
  private memoryCache: CachedUserLanguage | null = null;
  private loadPromise: Promise<CachedUserLanguage | null> | null = null;

  async getCachedLanguage(smartuserId: string): Promise<string | null> {
    if (this.memoryCache && this.memoryCache.smartuserId === smartuserId) {
      console.log('🌍 [UserLangCache] Memory cache hit for user:', smartuserId);
      return this.memoryCache.language;
    }

    try {
      const cached = await getStorageItem(CACHE_KEY);
      if (!cached) {
        console.log('🌍 [UserLangCache] No cached preference found');
        return null;
      }

      const parsed: CachedUserLanguage = JSON.parse(cached);

      if (parsed.smartuserId !== smartuserId) {
        console.log('🌍 [UserLangCache] Cache exists but for different user, clearing');
        await this.clear();
        return null;
      }

      this.memoryCache = parsed;
      console.log('🌍 [UserLangCache] Loaded cached language:', parsed.language, 'for user:', smartuserId);
      return parsed.language;
    } catch (error) {
      console.error('🌍 [UserLangCache] Error reading cache:', error);
      return null;
    }
  }

  async cacheLanguage(smartuserId: string, language: string): Promise<void> {
    const data: CachedUserLanguage = {
      smartuserId,
      language,
      cachedAt: Date.now(),
    };

    this.memoryCache = data;

    try {
      await setStorageItem(CACHE_KEY, JSON.stringify(data));
      console.log('🌍 [UserLangCache] Cached language:', language, 'for user:', smartuserId);
    } catch (error) {
      console.error('🌍 [UserLangCache] Error caching language:', error);
    }
  }

  async clear(): Promise<void> {
    this.memoryCache = null;
    try {
      await deleteStorageItem(CACHE_KEY);
      console.log('🌍 [UserLangCache] Cache cleared');
    } catch (error) {
      console.error('🌍 [UserLangCache] Error clearing cache:', error);
    }
  }

  async getSmartUserIdFromAuthToken(): Promise<string | null> {
    try {
      const tokenKey = Platform.OS === 'web' ? 'auth_token' : 'auth_token';
      const token = await getStorageItem(tokenKey);

      if (!token) {
        return null;
      }

      const userDataKey = 'user_data';
      const userData = await getStorageItem(userDataKey);

      if (userData) {
        const parsed = JSON.parse(userData);
        return parsed.smartuserId || null;
      }

      return null;
    } catch (error) {
      console.error('🌍 [UserLangCache] Error getting smartuserId from token:', error);
      return null;
    }
  }

  async preloadCachedLanguage(): Promise<{ smartuserId: string; language: string } | null> {
    try {
      const cached = await getStorageItem(CACHE_KEY);
      if (!cached) {
        return null;
      }

      const parsed: CachedUserLanguage = JSON.parse(cached);
      this.memoryCache = parsed;

      console.log('🌍 [UserLangCache] Preloaded cached language:', parsed.language);
      return { smartuserId: parsed.smartuserId, language: parsed.language };
    } catch (error) {
      console.error('🌍 [UserLangCache] Error preloading cache:', error);
      return null;
    }
  }
}

export const userLanguagePreferenceCache = new UserLanguagePreferenceCache();
